import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Phone, Calendar, Users, MapPin, MessageSquare, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { publicApi } from '../lib/api';

export default function ReservationPage() {
  const [searchParams] = useSearchParams();
  const preselectedTour = searchParams.get('tour');
  
  const [tours, setTours] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    tour: preselectedTour || '',
    date: null,
    persons: '2',
    hotel: '',
    notes: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [toursRes, settingsRes] = await Promise.all([
          publicApi.getTours(),
          publicApi.getSettings()
        ]);
        setTours(toursRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const generateWhatsAppUrl = () => {
    const whatsappNumber = settings?.contact?.whatsapp?.replace(/\D/g, '') || '18091234567';
    let template = settings?.whatsapp_template || 
      'Hola, Caribbean Groove Tours. Quiero reservar: {tour} para el día {date}, {persons} personas. Hotel/zona: {zone}. Tel: {phone}. Notas: {notes}. ¿Disponibilidad y total por favor?';
    
    const selectedTour = tours.find(t => t.slug === formData.tour);
    const tourName = selectedTour?.name || formData.tour || 'No especificado';
    const dateStr = formData.date ? format(formData.date, 'PPP', { locale: es }) : 'Por definir';
    
    const message = template
      .replace('{tour}', tourName)
      .replace('{date}', dateStr)
      .replace('{persons}', formData.persons)
      .replace('{zone}', formData.hotel || 'No especificado')
      .replace('{phone}', formData.phone)
      .replace('{notes}', formData.notes || 'Ninguna');

    return `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const url = generateWhatsAppUrl();
    window.open(url, '_blank');
    setSubmitted(true);
  };

  const isFormValid = formData.name && formData.phone && formData.tour;

  return (
    <div className="min-h-screen bg-stone-50" data-testid="reservation-page">
      <Navbar />
      
      {/* Header */}
      <section className="pt-32 pb-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <p className="text-sm font-medium tracking-widest uppercase text-primary mb-2">
            Reservaciones
          </p>
          <h1 className="text-4xl md:text-5xl font-serif text-stone-900 mb-4">
            Reserva tu Excursión
          </h1>
          <p className="text-stone-500 max-w-2xl mx-auto">
            Completa el formulario y te contactaremos por WhatsApp para confirmar 
            disponibilidad y coordinar todos los detalles.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12">
        <div className="max-w-2xl mx-auto px-4 md:px-8">
          {submitted ? (
            <div className="bg-white rounded-3xl p-8 md:p-12 text-center shadow-sm border border-stone-100">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h2 className="text-2xl font-serif text-stone-900 mb-4">
                ¡Solicitud Enviada!
              </h2>
              <p className="text-stone-600 mb-8">
                Tu mensaje ha sido enviado a WhatsApp. Te responderemos en minutos 
                para confirmar tu reservación.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => setSubmitted(false)}
                  variant="outline"
                  className="rounded-xl"
                >
                  Nueva Reservación
                </Button>
                <Link to="/excursiones">
                  <Button className="rounded-xl bg-primary">
                    Ver Más Excursiones
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-6 md:p-10 shadow-sm border border-stone-100">
              <div className="space-y-6">
                {/* Name */}
                <div>
                  <Label htmlFor="name" className="text-stone-700 font-medium">
                    Nombre completo *
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Tu nombre"
                    className="mt-2 h-12 rounded-xl"
                    required
                    data-testid="reservation-name"
                  />
                </div>

                {/* Phone */}
                <div>
                  <Label htmlFor="phone" className="text-stone-700 font-medium">
                    Teléfono / WhatsApp *
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="+1 809 123 4567"
                    className="mt-2 h-12 rounded-xl"
                    required
                    data-testid="reservation-phone"
                  />
                </div>

                {/* Tour Selection */}
                <div>
                  <Label className="text-stone-700 font-medium">
                    Excursión *
                  </Label>
                  <Select 
                    value={formData.tour} 
                    onValueChange={(value) => handleInputChange('tour', value)}
                  >
                    <SelectTrigger className="mt-2 h-12 rounded-xl" data-testid="reservation-tour">
                      <SelectValue placeholder="Selecciona una excursión" />
                    </SelectTrigger>
                    <SelectContent>
                      {tours.map(tour => (
                        <SelectItem key={tour.slug} value={tour.slug}>
                          {tour.name} - ${tour.price}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date */}
                <div>
                  <Label className="text-stone-700 font-medium">
                    Fecha preferida
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="mt-2 w-full h-12 rounded-xl justify-start text-left font-normal"
                        data-testid="reservation-date"
                      >
                        <Calendar className="mr-2 h-4 w-4 text-primary" />
                        {formData.date ? (
                          format(formData.date, 'PPP', { locale: es })
                        ) : (
                          <span className="text-stone-500">Selecciona una fecha</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={formData.date}
                        onSelect={(date) => handleInputChange('date', date)}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Persons */}
                <div>
                  <Label className="text-stone-700 font-medium">
                    Cantidad de personas
                  </Label>
                  <Select 
                    value={formData.persons} 
                    onValueChange={(value) => handleInputChange('persons', value)}
                  >
                    <SelectTrigger className="mt-2 h-12 rounded-xl" data-testid="reservation-persons">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5,6,7,8,9,10].map(n => (
                        <SelectItem key={n} value={String(n)}>
                          {n} persona{n > 1 ? 's' : ''}
                        </SelectItem>
                      ))}
                      <SelectItem value="11+">Más de 10 (grupo)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Hotel */}
                <div>
                  <Label htmlFor="hotel" className="text-stone-700 font-medium">
                    Hotel o zona de recogida
                  </Label>
                  <Input
                    id="hotel"
                    value={formData.hotel}
                    onChange={(e) => handleInputChange('hotel', e.target.value)}
                    placeholder="Ej: Hard Rock Punta Cana"
                    className="mt-2 h-12 rounded-xl"
                    data-testid="reservation-hotel"
                  />
                </div>

                {/* Notes */}
                <div>
                  <Label htmlFor="notes" className="text-stone-700 font-medium">
                    Notas adicionales
                  </Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    placeholder="¿Alguna solicitud especial? ¿Viajan niños?"
                    className="mt-2 rounded-xl resize-none"
                    rows={3}
                    data-testid="reservation-notes"
                  />
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  size="lg"
                  disabled={!isFormValid}
                  className="w-full rounded-xl bg-[#25D366] hover:bg-[#20BD5A] text-white gap-2 py-6 text-lg"
                  data-testid="reservation-submit"
                >
                  <Phone className="w-5 h-5" />
                  Enviar a WhatsApp
                </Button>

                <p className="text-center text-sm text-stone-500">
                  Al enviar, serás redirigido a WhatsApp con tu solicitud prellenada.
                </p>
              </div>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
